import React from 'react';
import { Link } from 'react-router-dom';
import PageLayout from '../../components/PageLayout';
import PageHero from '../../components/PageHero';
import Button from '../../components/Button';

const BREADCRUMBS = [
  { label: 'Home', to: '/' },
  { label: 'Employment', to: '/employment/ifys-recruitment' },
  { label: 'Volunteers' },
];

const VolunteersPage = () => {
  return (
    <PageLayout activeLink="employment">
      <PageHero
        title="Volunteers"
        backgroundImage="/images/thumb2.jpg"
        breadcrumbs={BREADCRUMBS}
        variant="split"
      />

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 md:py-20">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-ifys-blue mb-6 tracking-tight">
            Volunteers
          </h2>
          <p className="text-lg sm:text-xl text-ifys-blue/90 leading-relaxed">
            Volunteering can be a powerful driver of personal, social, economic, cultural and
            environmental change. It is a well-researched phenomenon that the happiest and
            healthiest people are those who do the most for others. IFYS acknowledges the amazing
            contribution Volunteers make and welcomes you to the first step in your volunteering
            career with IFYS.
          </p>
        </div>
      </section>

      <section className="bg-ifys-light-gray border-t border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <h3 className="text-2xl sm:text-3xl font-bold text-ifys-blue mb-6">
            Urban Angels Community Kitchen Volunteers
          </h3>

          <div className="space-y-4 text-gray-700 text-base sm:text-lg leading-relaxed">
            <p>
              The need for a Community Kitchen arose from understanding the challenges faced where,
              as a result of illness, trauma or other significant difficulty, a household routine is
              so disrupted that it becomes an ordeal to arrange a nutritious, balanced meal. The gift
              of a meal at these difficult times can change the dynamics within the household by not
              only relieving the stress of the moment but also uplifting the souls of those in need.
            </p>
            <p>
              Urban Angels offers a simple and practical way for people individually and collectively
              to contribute their natural goodwill, skills and energies to help those in need.
            </p>
            <p>
              We offer refreshments during your shifts and meals for volunteers who work during lunch
              and dinner service.
            </p>
          </div>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl border border-gray-200 p-6 sm:p-7 shadow-sm">
              <h4 className="text-lg font-bold text-ifys-blue mb-3">Maroochydore Kitchen</h4>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                Operating <span className="font-semibold">Monday to Friday, 9:00 am – 5:00 pm</span>.
                Volunteer shifts are flexible — please work with our Customer Service Administrator
                to select shifts in advance so we can comply with health and safety capacity
                requirements in the Kitchen.
              </p>
            </div>
            <div className="bg-white rounded-2xl border border-gray-200 p-6 sm:p-7 shadow-sm">
              <h4 className="text-lg font-bold text-ifys-blue mb-3">Bokarina Kitchen (The Hub)</h4>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                Operating every day, <span className="font-semibold">365 days a year, 10:00 am –
                8:00 pm</span>. Volunteers are most needed during lunch and dinner service. Recommended
                shifts: <span className="font-semibold">11:30 am – 2:00 pm</span> or{' '}
                <span className="font-semibold">5:00 pm – 7:00 pm</span>.
              </p>
            </div>
          </div>

          <p className="mt-8 text-base text-gray-700 leading-relaxed">
            Volunteers may choose to have regular or ad-hoc shifts. Rosters are published every
            fortnight for the upcoming month.
          </p>

          <div className="mt-10 p-5 sm:p-6 bg-white rounded-2xl border border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <p className="text-sm sm:text-base text-gray-700">
              For more information, please email{' '}
              <a
                href="mailto:volunteers@ifys.com.au?subject=Urban%20Angels%20Community%20Kitchen%20Volunteers"
                className="text-ifys-blue font-semibold hover:underline"
              >
                volunteers@ifys.com.au
              </a>.
            </p>
            <Button
              href="mailto:volunteers@ifys.com.au?subject=Urban%20Angels%20Community%20Kitchen%20Volunteers"
              variant="primary"
              size="md"
            >
              Email Volunteers Team
            </Button>
          </div>
        </div>
      </section>

      <section className="bg-white border-t border-gray-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
          <h2 className="text-center text-ifys-blue text-2xl sm:text-3xl font-bold mb-10">
            Explore Employment
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 max-w-3xl mx-auto">
            <Link
              to="/employment/ifys-recruitment"
              className="group relative block rounded-2xl overflow-hidden bg-white border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
            >
              <div className="px-8 py-10 flex flex-col items-center text-center gap-4">
                <div className="w-16 h-16 rounded-full bg-ifys-blue/10 flex items-center justify-center group-hover:bg-ifys-blue/20 transition-colors duration-300">
                  <svg className="w-8 h-8 text-ifys-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-ifys-blue">IFYS Careers</h3>
                <p className="text-sm text-gray-500 leading-relaxed">
                  Learn what it&rsquo;s like to work with us and what to expect when you apply.
                </p>
                <span className="inline-flex items-center gap-1 text-sm font-semibold text-ifys-blue group-hover:gap-2 transition-all duration-200">
                  Read more
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </span>
              </div>
            </Link>
            <Link
              to="/employment/current-vacancies"
              className="group relative block rounded-2xl overflow-hidden bg-white border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
            >
              <div className="px-8 py-10 flex flex-col items-center text-center gap-4">
                <div className="w-16 h-16 rounded-full bg-ifys-blue/10 flex items-center justify-center group-hover:bg-ifys-blue/20 transition-colors duration-300">
                  <svg className="w-8 h-8 text-ifys-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-ifys-blue">Current Vacancies</h3>
                <p className="text-sm text-gray-500 leading-relaxed">
                  Browse our latest job openings across Queensland.
                </p>
                <span className="inline-flex items-center gap-1 text-sm font-semibold text-ifys-blue group-hover:gap-2 transition-all duration-200">
                  View jobs
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </span>
              </div>
            </Link>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default VolunteersPage;
